import { Component, Inject, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ResponseFromExpress } from 'src/app/Interfaces/SQL-interfaces';
import { TruckDistributionUsers } from 'src/app/Interfaces/truck-distribution';
import { TruckLoadingUsers } from 'src/app/Interfaces/truck-loading';
import { AdminTypes } from 'src/app/Interfaces/users';
import { AlertService } from 'src/app/services/alert.service';
import { LoadingService } from 'src/app/services/loading.service';
import { TruckDistributionService } from 'src/app/services/truck-distribution.service';
import { UtilService } from 'src/app/services/util.service';

export type TruckDistributionDriversPopupMode = 'add' | 'edit' | 'delete';

type IncomingData = { mode: 'add' } | { mode: 'edit'; row: TruckDistributionUsers } | { mode: 'delete'; row: TruckDistributionUsers };

type FieldInfo = {
	text: string;
	display: boolean;
};

@Component({
	selector: 'app-truck-distribution-drivers-popup',
	templateUrl: './truck-distribution-drivers-popup.component.html',
	styleUrls: ['./truck-distribution-drivers-popup.component.scss'],
})
export class TruckDistributionDriversPopupComponent implements OnInit {
	_loading = inject(LoadingService);
	private fb = inject(FormBuilder);
	private _truckDistribution = inject(TruckDistributionService);
	private _alert = inject(AlertService);
	private _util = inject(UtilService);

	drivers$ = this._truckDistribution.drivers$;
	existingAgentIDs = new Set(this.drivers$.value.map(driver => driver.AgentID));

	incomingDriver: TruckDistributionUsers | null = null;

	form = this.fb.group({
		agentid: ['', [Validators.required, this.validateAgentID(), Validators.pattern(/^\d+$/), Validators.maxLength(3)]],
		username: ['', [Validators.required, Validators.maxLength(50)]],
		password: ['', [Validators.required, Validators.maxLength(50)]],
		deliveryLine: ['', [Validators.maxLength(50)]],
		carNumber: ['', [Validators.maxLength(50)]],
		adminType: [<'nahag' | 'podmanager'>'nahag', Validators.required],
	});

	allFields: Record<keyof typeof this.form.controls, FieldInfo> = {
		agentid: {
			text: 'מספר נהג',
			display: true,
		},
		username: {
			text: 'שם נהג',
			display: true,
		},
		password: {
			text: 'סיסמא',
			display: true,
		},
		adminType: {
			text: 'סוג משתמש',
			display: false,
		},
		carNumber: {
			text: 'מספר רכב',
			display: true,
		},
		deliveryLine: {
			text: 'קו חלוקה',
			display: true,
		},
	};

	mode$ = new BehaviorSubject<TruckDistributionDriversPopupMode>(this.data.mode);
	title$ = this.mode$.pipe(
		map(mode => {
			switch (mode) {
				case 'add':
					return 'נהג חדש';
				case 'edit':
					return 'ערוך נהג';
				case 'delete':
					return 'מחיקת נהג';
				default:
					return '';
			}
		}),
	);

	buttonTitle$ = this.mode$.pipe(
		map(mode => {
			switch (mode) {
				case 'add':
					return 'הוסף';
				case 'edit':
					return 'עדכן';
				case 'delete':
					return 'מחק';
				default:
					return '';
			}
		}),
	);

	fields = Object.keys(this.allFields) as (keyof typeof this.allFields)[];

	constructor(@Inject(MAT_DIALOG_DATA) private data: IncomingData, private dialogRef: MatDialogRef<TruckDistributionDriversPopupComponent>) {}

	ngOnInit(): void {
		if (this.data.mode === 'add') return;

		this.incomingDriver = this.data.row;

		this.populateForm();

		if (this.data.mode === 'edit' && this.incomingDriver.AgentID) {
			this.existingAgentIDs.delete(this.incomingDriver.AgentID);
		}

		if (this.data.mode === 'delete') {
			this.form.disable();
		}
	}

	populateForm() {
		this.form.patchValue({
			agentid: this.incomingDriver.AgentID,
			username: this.incomingDriver.UserName,
			password: this.incomingDriver.Password || '',
			deliveryLine: this.incomingDriver.DeliveryLine || '',
			carNumber: this.incomingDriver.CarNum || '',
			adminType: (this.incomingDriver.Admin || 'nahag') as 'nahag' | 'podmanager',
		});
	}

	validateAgentID() {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.value && this.existingAgentIDs.has(control.value)) {
				return { agentIDExists: true };
			}
			return null;
		};
	}

	getFormErrorMessage(field: keyof typeof this.form.controls) {
		if (this.form.controls[field].hasError('maxlength')) return 'ערך ארוך מדי';
		if (field !== 'agentid' || this.form.controls.agentid.hasError('required')) return 'שדה חובה';

		if (this.form.controls.agentid.hasError('pattern')) {
			return 'חייב להיות מספר';
		}
		if (this.form.controls.agentid.hasError('agentIDExists')) {
			return 'מספר זה כבר קיים';
		}
		if (this.form.controls.agentid.hasError('maxlength')) {
			return 'לא ניתן להקליד יותר מ3 ספרות';
		}
		return '';
	}

	async handleAction() {
		if (this.form.invalid) return;

		if (this.mode$.value === 'delete') {
			const confirm = await this._alert.customConfirmAlert('האם אתה בטוח שברצונך למחוק את הנהג?');
			if (!confirm) return;
		}

		this._loading.changeLoadingStatuses({ isLoading: true, newMessage: 'שולח עידכון...' });

		let observable: Observable<ResponseFromExpress<TruckDistributionUsers>>;

		// const { AgentID, Admin, UserName, Password, DeliveryLine, CarNum } = body;

		const body = {
			AgentID: this.form.value.agentid,
			UserName: this.form.value.username,
			Password: this.form.value.password,
			Admin: this.form.value.adminType as AdminTypes,
			DeliveryLine: this.form.value.deliveryLine,
			CarNum: this.form.value.carNumber,
		};

		switch (this.mode$.value) {
			case 'add':
				observable = this._truckDistribution.handleCreateNewUser(body);
				break;
			case 'edit':
				observable = this._truckDistribution.handleEditExistingUser(body);
				break;
			case 'delete':
				observable = this._truckDistribution.handleDeleteUser(body.AgentID);
				break;
		}

		this._util.pipeThrowIfSQLErrorToSubscriber(observable).subscribe({
			next: res => {
				this._alert.customAlert('הפעולה בוצעה בהצלחה');

				this._truckDistribution.updateDistributionUsers();
				this.dialogRef.close();

				this._loading.changeLoadingStatuses({ isLoading: false });
			},
			error: err => {
				this._loading.changeLoadingStatuses({ isLoading: false });
				alert('לא היה ניתן לבצע את הפעולה');
			},
		});
	}
}
